import { useCallback, useEffect, useState } from 'react';
import { useUser } from '@dfl/react-security';
import PushNotificationService from 'modules/notification/services/push-notification.service';
import { isPushSubscribed, setPushSubscribed } from 'modules/notification/services/device-id.service';

export const usePushNotifications = () => {
  const { user } = useUser();
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [subscribed, setSubscribed] = useState<boolean>(() => isPushSubscribed());
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isPromptDismissed, setIsPromptDismissed] = useState(false);

  const enablePushNotifications = useCallback(async () => {
    setIsPromptDismissed(true);
    setIsEnabling(true);
    try {
      return await PushNotificationService.register(true);
    } finally {
      setPermission(Notification.permission);
      setSubscribed(isPushSubscribed());
      setIsEnabling(false);
    }
  }, []);

  const disablePushNotifications = useCallback(async () => {
    setIsDisabling(true);
    try {
      await PushNotificationService.unsubscribe();
    } finally {
      setSubscribed(isPushSubscribed());
      setIsDisabling(false);
    }
  }, []);

  // On login, re-register the device (clears revokedAt from logout) and sync the
  // toggle with the backend. Never calls /subscribe — the user's preference is
  // preserved. Silent: only re-registers devices that already had a token.
  useEffect(() => {
    if (!user) return;
    let active = true;
    PushNotificationService.syncOnLogin()
      .then((status) => {
        if (!active) return;
        setSubscribed(status.subscribed);
        setPushSubscribed(status.subscribed);
      })
      .catch((error) => { console.error('Unable to sync push subscription status', error); });
    return () => { active = false; };
  }, [user]);

  return {
    enablePushNotifications,
    disablePushNotifications,
    isEnabling,
    isDisabling,
    isSubscribed: subscribed && permission === 'granted',
    shouldPrompt: Boolean(user) && permission !== 'denied' && !subscribed && !isPromptDismissed,
  };
};
